import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

// =============================================================================
// DataTable primitives — enterprise table behaviour without a rewrite
// =============================================================================
// These wrap the existing hand-written <table> markup so we get contained
// scrolling + sticky headers consistently, while each page keeps full control
// of its columns and cell rendering.
//
//   <DataTableCard footer={<Pagination …/>}>     ← height-bounded card; only the
//     <table className="kc-table">                  body scrolls (desktop), the
//       <thead className={STICKY_HEAD}> … </thead>   pagination footer stays pinned
//       <tbody> … </tbody>
//     </table>
//   </DataTableCard>
//
// On desktop the card fills the remaining page height (use inside a
// PageScaffold with scroll={false}); below `lg` it relaxes to natural height so
// the page scrolls normally.

/** Apply to <thead> so the column headers stay visible while the body scrolls. */
export const STICKY_HEAD = 'sticky top-0 z-10 bg-paper-2';

/**
 * Height-bounded table card. Place inside a PageScaffold (scroll={false}); it
 * flexes to fill leftover height and scrolls its own body. The optional footer
 * (typically <Pagination/>) is pinned below the scroll region.
 */
export function DataTableCard({
  children,
  footer,
  className,
  scrollClassName,
}: {
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  scrollClassName?: string;
}): JSX.Element {
  return (
    <section
      className={cn(
        'bg-surface border border-rule rounded-lg shadow-sm flex flex-col overflow-hidden',
        'md:flex-1 md:min-h-0',
        className,
      )}
    >
      <div className={cn('md:flex-1 md:min-h-0 md:overflow-auto', scrollClassName)}>{children}</div>
      {footer && <div className="shrink-0 border-t border-rule-2">{footer}</div>}
    </section>
  );
}

/**
 * Standalone contained scroll region for tables that aren't the page's primary
 * full-height table (e.g. a secondary table inside a detail card). Caps height
 * and scrolls internally on all breakpoints.
 */
export function TableScroll({
  children,
  className,
  maxHeight = 'max-h-[28rem]',
}: {
  children: ReactNode;
  className?: string;
  maxHeight?: string;
}): JSX.Element {
  return <div className={cn('overflow-auto', maxHeight, className)}>{children}</div>;
}
